import { usePostProductItemApi, usePutProductItemApi } from 'api/product_item'
import { GlobalStateContext } from 'contexts/global_state_context'
import { useContext, useEffect, useState } from 'react'
import { Badge, Button, Modal, Table } from 'react-bootstrap'
import { Form, useEffectSkipFirst, useForm } from 'utils/hooks'
import { ApiError } from 'utils/network/api_hooks'
import { ProductItem, ProductItemForm } from 'entities/product_item'
import { Flex } from 'components/shared/flex'
import { InputField, SelectField } from 'components/shared/input'
import { ProtocolVersionProbes, ProtocolVersionSearchForm } from 'entities/protocol_version'
import { useFetchProtocolVersionsApi } from 'api/protocol_version'
import SelectProbeModal from 'components/probe/select_probe_modal'
import { Probe } from 'entities/probe'

type ProductItemFormViewProps = {
    form: Form<ProductItemForm>
    apiError: ApiError
    isEdit?: boolean
}

export const ProductItemFormView = ({ form, apiError, isEdit }: ProductItemFormViewProps) => {
    const qcVersionsForm = useForm<ProtocolVersionSearchForm>({ onlyQc: true })
    const qcVersionsApi = useFetchProtocolVersionsApi(qcVersionsForm)

    useEffect(() => {
        qcVersionsApi.execute()
    }, [])

    const productNames = [
        { ja: '卵巣がん', en: 'ovary' },
        { ja: '肺がん', en: 'lung' },
        { ja: '乳がん', en: 'breast' },
        { ja: '膵がん', en: 'pancreas' },
        { ja: '胃がん', en: 'stomach' },
        { ja: '大腸がん', en: 'colorectal' },
        { ja: '食道がん', en: 'esophagus' },
    ]

    useEffectSkipFirst(() => {
        const nameEn = productNames.find((i) => i.ja === form.object.name)?.en
        if (nameEn) {
            form.updateObject('nameEn', nameEn)
        }
    }, [form.object.name])

    return (
        <>
            {!form.object.isStandardQc && !form.object.isRnaStandardQc && (
                <Flex>
                    <SelectField
                        form={form}
                        attr="name"
                        apiError={apiError}
                        label="名称"
                        required
                        selectItems={productNames.map((item) => {
                            return { label: item.ja, value: item.ja }
                        })}
                        includeBlank
                    />
                </Flex>
            )}
            <InputField form={form} attr="color" apiError={apiError} label="プロダクトカラー" required type="color" />
            <Flex>
                {isEdit && (
                    <SelectField
                        key={form.object.id}
                        form={form}
                        attr="currentProtocolVersionId"
                        selectItems={form.object.protocolversionSet!.map((qv) => {
                            return { label: `ver.${qv.number}`, value: qv.id! }
                        })}
                        label="プロトコルバージョン"
                        includeBlank
                        required
                    />
                )}
                {!form.object.isStandardQc && !form.object.isRnaStandardQc && (
                    <SelectField
                        form={form}
                        attr="qcVersionId"
                        selectItems={qcVersionsApi.response.results.map((qv) => {
                            return { label: `ver.${qv.number}`, value: qv.id! }
                        })}
                        label={isEdit ? 'QCバージョン' : 'QCのバージョンを選択してください'}
                        includeBlank
                        required
                    />
                )}
            </Flex>
            <Flex></Flex>

            {!isEdit && (
                <>
                    <div style={{ marginTop: 20 }}>
                        {form.object.protocolVersionProbes && form.object.protocolVersionProbes?.length > 0 && (
                            <div style={{ marginTop: 20 }}>
                                <div style={{ padding: 10 }}>
                                    <Table style={{ verticalAlign: 'middle' }}>
                                        <thead>
                                            <tr>
                                                <th style={{ width: 200 }}>Probe</th>
                                                <th style={{ minWidth: 200 }}>Tags</th>
                                                <th>Min range</th>
                                                <th>Max range</th>
                                                {/* <th>QC_TYPE</th> */}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {form.object.protocolVersionProbes?.map((item, index) => {
                                                return (
                                                    <tr key={'probes-' + index}>
                                                        <td>
                                                            <div>{item.probe?.name}</div>
                                                        </td>
                                                        <td>
                                                            {item.probe?.tags?.map((tag) => {
                                                                return (
                                                                    <Badge bg="secondary" key={tag.id}>
                                                                        {tag.name}
                                                                    </Badge>
                                                                )
                                                            })}
                                                        </td>
                                                        <td>
                                                            <InputField
                                                                style={{ marginBottom: 0 }}
                                                                form={form}
                                                                attr={['protocolVersionProbes', index, 'minRange']}
                                                                type="number"
                                                                required
                                                            />
                                                        </td>
                                                        <td>
                                                            <InputField
                                                                style={{ marginBottom: 0 }}
                                                                form={form}
                                                                attr={['protocolVersionProbes', index, 'maxRange']}
                                                                type="number"
                                                                required
                                                            />
                                                        </td>
                                                        {/* <td>
                                                <SelectField
                                                    style={{ marginBottom: 0 }}
                                                    form={form}
                                                    attr={['probeForms', index, 'sortNumber']}
                                                    selectItems={QC_TYPE_SELECT_ITEMS}
                                                    includeBlank
                                                />
                                            </td> */}
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                            </div>
                        )}
                    </div>

                    <SelectProbeModal
                        openButtonStyle={{ width: '100%', marginTop: 10 }}
                        openButtonText="Probe選択"
                        executeButtonText="追加"
                        onClick={(probes: Probe[]) => {
                            form.update((f) => {
                                f.protocolVersionProbes = probes.map((p) => {
                                    return { probe: p }
                                }) as ProtocolVersionProbes[]
                            })
                        }}
                        initialItems={form.object.probes}
                    />
                </>
            )}
        </>
    )
}

type NewProductItemFormProps = {
    onComplete?: () => void
}

export const NewProductItemForm = (props: NewProductItemFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePostProductItemApi()
    const form = useForm<ProductItemForm>({})
    const [show, setShow] = useState<boolean>(false)

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                setShow(false)
                props.onComplete()
            }
        }
    }, [api.loading])

    const handleClose = () => {
        setShow(false)
    }

    return (
        <>
            <Button
                variant="success"
                onClick={() => {
                    setShow(true)
                }}
            >
                新規登録
            </Button>
            <Modal show={show} onHide={handleClose} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>がん検査の登録</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ProductItemFormView form={form} apiError={api.apiError} />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        閉じる
                    </Button>
                    <Button variant="success" onClick={handleUpdate}>
                        登録
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}
type EditProductItemFormProps = {
    productItem: ProductItem
    show: boolean
    onClose: () => void
    onComplete?: () => void
}

export const EditProductItemForm = (props: EditProductItemFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePutProductItemApi()
    const form = useForm<ProductItemForm>(props.productItem)

    useEffectSkipFirst(() => {
        form.set(props.productItem)
    }, [props.productItem])

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                props.onComplete()
            }
        }
    }, [api.loading])

    return (
        <Modal show={props.show} onHide={props.onClose}>
            <Modal.Header closeButton>
                <Modal.Title>がん検査の編集</Modal.Title>
            </Modal.Header>
            <Modal.Body>{form.object.id && <ProductItemFormView form={form} apiError={api.apiError} isEdit />}</Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={props.onClose}>
                    閉じる
                </Button>
                <Button variant="success" onClick={handleUpdate}>
                    更新
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
